import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  DndContext, useDraggable, useDroppable, PointerSensor, TouchSensor, useSensor, useSensors, DragOverlay,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowLeft, GitBranch, GripVertical, Loader2, CheckCircle2, XCircle, Sparkles, Coins, RotateCcw, X,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fireBigConfetti, fireConfetti } from "@/lib/confetti";

const ACCENT = {
  indigo: {
    text: "text-indigo-400", textLight: "text-indigo-300",
    chipFilled: "bg-indigo-500/15 border-indigo-400/40 text-white",
    chipHover: "hover:border-indigo-400/40 hover:bg-white/[0.07]",
    isOver: "border-indigo-400 bg-indigo-500/10", isOverPool: "border-indigo-400 bg-indigo-500/5",
    resultBorder: "border-indigo-400/30",
  },
  teal: {
    text: "text-teal-400", textLight: "text-teal-300",
    chipFilled: "bg-teal-500/15 border-teal-400/40 text-white",
    chipHover: "hover:border-teal-400/40 hover:bg-white/[0.07]",
    isOver: "border-teal-400 bg-teal-500/10", isOverPool: "border-teal-400 bg-teal-500/5",
    resultBorder: "border-teal-400/30",
  },
  fuchsia: {
    text: "text-fuchsia-400", textLight: "text-fuchsia-300",
    chipFilled: "bg-fuchsia-500/15 border-fuchsia-400/40 text-white",
    chipHover: "hover:border-fuchsia-400/40 hover:bg-white/[0.07]",
    isOver: "border-fuchsia-400 bg-fuchsia-500/10", isOverPool: "border-fuchsia-400 bg-fuchsia-500/5",
    resultBorder: "border-fuchsia-400/30",
  },
  amber: {
    text: "text-amber-400", textLight: "text-amber-300",
    chipFilled: "bg-amber-500/15 border-amber-400/40 text-white",
    chipHover: "hover:border-amber-400/40 hover:bg-white/[0.07]",
    isOver: "border-amber-400 bg-amber-500/10", isOverPool: "border-amber-400 bg-amber-500/5",
    resultBorder: "border-amber-400/30",
  },
  emerald: {
    text: "text-emerald-400", textLight: "text-emerald-300",
    chipFilled: "bg-emerald-500/15 border-emerald-400/40 text-white",
    chipHover: "hover:border-emerald-400/40 hover:bg-white/[0.07]",
    isOver: "border-emerald-400 bg-emerald-500/10", isOverPool: "border-emerald-400 bg-emerald-500/5",
    resultBorder: "border-emerald-400/30",
  },
};

function NodeChip({ node, filled, theme }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: node.id });
  const style = {
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    opacity: isDragging ? 0.35 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      data-testid={`flow-node-chip-${node.id}`}
      className={`group flex items-center gap-2 rounded-xl border px-3.5 py-3 cursor-grab active:cursor-grabbing select-none transition-colors ${
        filled ? theme.chipFilled : `bg-white/[0.04] border-white/10 text-slate-200 ${theme.chipHover}`
      }`}
    >
      <GripVertical className="w-3.5 h-3.5 text-slate-500 shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-tight truncate">{node.label}</p>
        <p className="text-[11px] text-slate-400 leading-snug mt-0.5 line-clamp-2">{node.description}</p>
      </div>
    </div>
  );
}

function SlotDropzone({ index, node, feedbackItem, onRemove, theme }) {
  const { setNodeRef, isOver } = useDroppable({ id: `slot-${index}` });
  const status = feedbackItem
    ? feedbackItem.correct
      ? "correct"
      : "incorrect"
    : null;

  return (
    <div className="flex items-start gap-3" data-testid={`flow-slot-${index}`}>
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-1 ${
          status === "correct"
            ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40"
            : status === "incorrect"
            ? "bg-red-500/20 text-red-300 border border-red-400/40"
            : "bg-white/5 text-slate-400 border border-white/10"
        }`}
      >
        {index + 1}
      </div>
      <div
        ref={setNodeRef}
        className={`flex-1 min-h-[64px] rounded-xl border-2 border-dashed flex items-center px-1 py-1 transition-colors ${
          isOver
            ? theme.isOver
            : status === "correct"
            ? "border-emerald-400/50 bg-emerald-500/5"
            : status === "incorrect"
            ? "border-red-400/50 bg-red-500/5"
            : "border-white/15 bg-white/[0.02]"
        }`}
      >
        {node ? (
          <div className="w-full flex items-center gap-2 px-2">
            <div className="flex-1">
              <NodeChip node={node} filled theme={theme} />
            </div>
            {!feedbackItem && (
              <button
                onClick={() => onRemove(index)}
                data-testid={`flow-slot-remove-${index}`}
                className="text-slate-500 hover:text-red-400 shrink-0"
                title="Remove"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {status === "correct" && <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />}
            {status === "incorrect" && <XCircle className="w-5 h-5 text-red-400 shrink-0" />}
          </div>
        ) : (
          <span className="text-xs text-slate-500 px-3">Drop pipeline stage here</span>
        )}
      </div>
      {status === "incorrect" && (
        <div className="hidden sm:block max-w-[180px] text-[11px] text-red-300/80 mt-2" data-testid={`flow-slot-hint-${index}`}>
          Correct: {feedbackItem.correct_label}
        </div>
      )}
    </div>
  );
}

function PoolArea({ nodes, theme }) {
  const { setNodeRef, isOver } = useDroppable({ id: "pool" });
  return (
    <div
      ref={setNodeRef}
      data-testid="flow-node-pool"
      className={`grid grid-cols-1 sm:grid-cols-2 gap-2.5 p-3 rounded-2xl border-2 border-dashed min-h-[100px] transition-colors ${
        isOver ? theme.isOverPool : "border-white/10"
      }`}
    >
      {nodes.length === 0 && (
        <p className="text-xs text-slate-500 col-span-full text-center py-6">All stages placed. Review the sequence and submit!</p>
      )}
      {nodes.map((n) => (
        <NodeChip key={n.id} node={n} theme={theme} />
      ))}
    </div>
  );
}

export default function FlowBuilderCanvas({ apiPrefix = "/flow-builder", backPath = "/flow-builder", accent = "indigo" }) {
  const theme = ACCENT[accent];
  const { id } = useParams();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } })
  );

  useEffect(() => {
    api
      .get(`${apiPrefix}/scenarios/${id}`)
      .then((res) => {
        setScenario(res.data);
        setSlots(new Array(res.data.slot_count).fill(null));
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load scenario"))
      .finally(() => setLoading(false));
  }, [id, apiPrefix]);

  const nodeById = useCallback((nid) => scenario?.nodes.find((n) => n.id === nid), [scenario]);

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const nodeId = active.id;
    const overId = over.id;
    setSlots((prev) => {
      const next = [...prev];
      const currentIndex = next.findIndex((n) => n === nodeId);
      if (currentIndex !== -1) next[currentIndex] = null;
      if (typeof overId === "string" && overId.startsWith("slot-")) {
        const idx = parseInt(overId.replace("slot-", ""), 10);
        next[idx] = nodeId;
      }
      return next;
    });
  };

  const handleRemove = (index) => {
    setSlots((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  const handleReset = () => {
    setSlots(new Array(scenario.slot_count).fill(null));
    setResult(null);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`${apiPrefix}/scenarios/${id}/submit`, { sequence: slots });
      setResult(data);
      if (data.solved) {
        data.reward_granted ? fireBigConfetti() : fireConfetti();
      } else {
        toast.error(`Not quite — ${data.correct_count}/${data.total} correct. Review the hints below.`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit sequence");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="flow-canvas-loading">
        <Loader2 className={`w-8 h-8 ${theme.text} animate-spin`} />
      </div>
    );
  }

  if (!scenario) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="flow-canvas-not-found">
        Scenario not found.
      </div>
    );
  }

  const placedIds = slots.filter(Boolean);
  const poolNodes = scenario.nodes.filter((n) => !placedIds.includes(n.id));
  const allFilled = slots.every(Boolean);
  const activeNode = activeId ? nodeById(activeId) : null;
  const feedbackByIndex = result?.feedback || null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="flow-builder-canvas-page">
      <Link to={backPath} data-testid="flow-canvas-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to scenarios
      </Link>

      <div className="flex items-center gap-3 mb-1">
        <GitBranch className={`w-5 h-5 ${theme.text}`} />
        <span className={`text-xs font-bold uppercase tracking-[0.2em] ${theme.text}`}>{scenario.difficulty}</span>
      </div>
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">{scenario.title}</h1>
      <p className="text-sm text-slate-400 mt-2 max-w-2xl">{scenario.description}</p>
      <div className="flex items-center gap-4 mt-3">
        <span className="text-xs font-bold text-[#FBBF24] flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> +{scenario.xp_reward} XP</span>
        <span className={`text-xs font-bold ${theme.textLight} flex items-center gap-1`}><Coins className="w-3.5 h-3.5" /> +{scenario.coins_reward} coins</span>
      </div>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-8">
          <div className="lg:col-span-2">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Available Stages ({poolNodes.length})</h2>
            <PoolArea nodes={poolNodes} theme={theme} />
          </div>

          <div className="lg:col-span-3">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Build the Pipeline — In Order</h2>
            <div className="space-y-3" data-testid="flow-slots-container">
              {slots.map((nodeId, index) => (
                <SlotDropzone
                  key={index}
                  index={index}
                  node={nodeId ? nodeById(nodeId) : null}
                  feedbackItem={feedbackByIndex ? feedbackByIndex[index] : null}
                  onRemove={handleRemove}
                  theme={theme}
                />
              ))}
            </div>
          </div>
        </div>

        <DragOverlay>{activeNode ? <NodeChip node={activeNode} filled theme={theme} /> : null}</DragOverlay>
      </DndContext>

      {!result && (
        <div className="flex items-center gap-3 mt-8">
          <button
            onClick={handleSubmit}
            disabled={!allFilled || submitting}
            data-testid="flow-canvas-submit-btn"
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitBranch className="w-4 h-4" />}
            Submit Sequence
          </button>
          <button
            onClick={handleReset}
            data-testid="flow-canvas-reset-btn"
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-slate-300 font-semibold px-5 py-3 rounded-xl transition-colors"
          >
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
        </div>
      )}

      {result && (
        <div className={`glass-panel rounded-2xl p-6 mt-8 border ${theme.resultBorder} text-center`} data-testid="flow-canvas-result">
          {result.solved ? (
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          ) : (
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          )}
          <h3 className="font-heading text-xl font-semibold text-white">
            {result.solved ? "Pipeline Assembled Correctly!" : "Not Quite Right"}
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            You scored {result.score}% ({result.correct_count}/{result.total} correct)
          </p>
          {result.reward_granted && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <span className="flex items-center gap-1 text-sm font-semibold text-[#FBBF24]"><Sparkles className="w-4 h-4" /> +{result.xp_reward} XP</span>
              <span className="flex items-center gap-1 text-sm font-semibold text-[#FBBF24]"><Coins className="w-4 h-4" /> +{result.coins_reward} Coins</span>
            </div>
          )}
          <div className="flex items-center justify-center gap-3 mt-6">
            {!result.solved && (
              <button
                onClick={handleReset}
                data-testid="flow-canvas-try-again-btn"
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors"
              >
                <RotateCcw className="w-4 h-4" /> Try Again
              </button>
            )}
            <button
              onClick={() => navigate(backPath)}
              data-testid="flow-canvas-back-to-list-btn"
              className="bg-white/5 hover:bg-white/10 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors"
            >
              Back to Scenarios
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
